import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, Keyboard, HeartPulse, User, Check } from "lucide-react";
import { getSelectedWorkerId, setSelectedWorkerId } from "@/lib/workerStore";

function WorkerListRow({ worker, isSelected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(worker.id)}
      data-testid={`select-employee-${worker.id}`}
      className={`w-full flex items-center justify-between p-3 hover:bg-slate-50 ${
        isSelected ? "bg-slate-100" : ""
      }`}
    >
      <span className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center">
          <User className="h-4 w-4" />
        </span>
        <span className="text-left">
          <span className="block font-medium text-slate-900">{worker.name}</span>
          {worker.employee_code && (
            <span className="block text-xs text-slate-500 font-mono">
              {worker.employee_code}
            </span>
          )}
        </span>
      </span>
      {isSelected && <Check className="h-5 w-5 text-emerald-600" />}
    </button>
  );
}

function WorkerList({ workers, loading, filter, selectedId, onSelect }) {
  if (loading) return <p className="p-4 text-slate-500">Loading…</p>;

  const filtered = filter
    ? workers.filter((w) =>
        w.name.toLowerCase().includes(filter.toLowerCase())
      )
    : workers;

  if (filtered.length === 0) {
    return <p className="p-4 text-slate-500 text-sm">No employees found.</p>;
  }

  return (
    <>
      {filtered.map((w) => (
        <WorkerListRow
          key={w.id}
          worker={w}
          isSelected={selectedId === w.id}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

export default function SelectWorker() {
  const navigate = useNavigate();
  const fetcher = useCallback(() => api.get("/workers").then((r) => r.data), []);
  const { data, loading } = useFetch(fetcher);
  const workers = data || [];
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedIdState] = useState(getSelectedWorkerId());

  const select = (id) => {
    setSelectedWorkerId(id);
    setSelectedIdState(id);
  };

  // Clear stale selection if the worker no longer exists.
  useEffect(() => {
    if (!data || !selectedId) return;
    if (!data.some((w) => w.id === selectedId)) {
      setSelectedIdState("");
    }
  }, [data, selectedId]);

  const selected = workers.find((w) => w.id === selectedId);

  return (
    <div className="space-y-5" data-testid="select-worker-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Step 1
        </p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Who is using this device?
        </h1>
      </div>

      <Card className="p-4 border-slate-200">
        <Input
          placeholder="Search by name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-12 text-base"
          data-testid="worker-search-input"
        />
        <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-slate-100">
          <WorkerList
            workers={workers}
            loading={loading}
            filter={filter}
            selectedId={selectedId}
            onSelect={select}
          />
        </div>
      </Card>

      {selected && (
        <Card className="p-4 border-slate-900 bg-slate-900 text-white">
          <p className="text-xs uppercase tracking-wider text-slate-400">
            Active employee
          </p>
          <p className="font-display text-2xl font-bold mt-1">{selected.name}</p>
        </Card>
      )}

      <div className="space-y-3">
        <Button
          disabled={!selectedId}
          onClick={() => navigate("/worker/scan")}
          className="w-full touch-btn h-16 bg-slate-900 hover:bg-slate-800 text-white text-lg font-bold rounded-xl gap-3 active:scale-[0.98]"
          data-testid="worker-scan-btn"
        >
          <ScanLine className="h-6 w-6" /> Scan tool QR code
        </Button>
        <Button
          disabled={!selectedId}
          variant="outline"
          onClick={() => navigate("/worker/manual")}
          className="w-full touch-btn h-16 border-2 border-slate-900 text-slate-900 text-lg font-bold rounded-xl gap-3"
          data-testid="worker-manual-btn"
        >
          <Keyboard className="h-6 w-6" /> Enter tool code manually
        </Button>
        <Button
          disabled={!selectedId}
          variant="outline"
          onClick={() => navigate("/worker/checkin")}
          className="w-full touch-btn h-16 border-2 border-slate-300 text-slate-800 text-lg font-bold rounded-xl gap-3"
          data-testid="worker-checkin-btn"
        >
          <HeartPulse className="h-6 w-6" /> Hand-health check-in
        </Button>
      </div>
    </div>
  );
}
